import { defineCommands, type CommandsOf, getAllEntities, bumpVersions } from '../../commands/defineCommands';
import type { CanvasStoreContext } from '../../types';
import type { CanvasEntity, ConnectorEndpoint, SegmentType, ConnectorData } from '../../entityTypes';
import { getConnectorData } from '../../entityTypes';

function isSelfLoopEndpoint(from: ConnectorEndpoint, to: ConnectorEndpoint): boolean {
  return from.entityId === to.entityId && from.side === to.side;
}

export const connectorCommands = defineCommands({
  'connector.add': (ctx: CanvasStoreContext, event: { entity: CanvasEntity }): CanvasStoreContext => {
    if (event.entity.kind !== 'connector') return ctx;
    const cd = getConnectorData(event.entity);
    if (isSelfLoopEndpoint(cd.from, cd.to)) return ctx;
    if (ctx.connectors[event.entity.id]) return ctx;
    const all = getAllEntities(ctx);
    const maxZ = all.reduce((m, e) => Math.max(m, e.zIndex ?? 0), 0);
    const entity = { ...event.entity, zIndex: event.entity.zIndex ?? maxZ + 1 };
    return {
      ...ctx,
      connectors: { ...ctx.connectors, [entity.id]: entity },
      ...bumpVersions(ctx, 'connectors'),
    };
  },

  'connector.setType': (ctx: CanvasStoreContext, event: { connectorId: string; segmentType: SegmentType }): CanvasStoreContext => {
    const e = ctx.connectors[event.connectorId];
    if (!e || e.kind !== 'connector') return ctx;
    return {
      ...ctx,
      connectors: { ...ctx.connectors, [event.connectorId]: { ...e, data: { ...e.data, segmentType: event.segmentType } } },
    };
  },

  'connector.split': (ctx: CanvasStoreContext, event: { connectorId: string; waypointId: string; x: number; y: number }): CanvasStoreContext => {
    const original = ctx.connectors[event.connectorId];
    if (!original || original.kind !== 'connector') return ctx;

    const cd = getConnectorData(original);
    const wpEndpoint: ConnectorEndpoint = { entityId: event.waypointId, side: 'center' };

    const all = getAllEntities(ctx);
    const maxZ = all.reduce((m, e) => Math.max(m, e.zIndex ?? 0), 0);

    const newWaypoint: CanvasEntity = {
      id: event.waypointId,
      kind: 'waypoint',
      x: event.x,
      y: event.y,
      width: 0,
      height: 0,
      layerId: '',
      connectable: false,
      zIndex: maxZ + 1,
      data: {},
    };

    const seg1Id = `conn-${Date.now()}-${Math.random().toString(36).slice(2, 4)}`;
    const seg2Id = `conn-${Date.now()}-${Math.random().toString(36).slice(4, 8)}`;

    const seg1: CanvasEntity = {
      id: seg1Id,
      kind: 'connector',
      x: 0, y: 0, width: 0, height: 0,
      layerId: '',
      connectable: false,
      zIndex: maxZ + 2,
      data: {
        from: cd.from,
        to: wpEndpoint,
        segmentType: cd.segmentType,
        strokeColor: cd.strokeColor,
        strokeWidth: cd.strokeWidth,
        strokeStyle: cd.strokeStyle,
        arrowStart: cd.arrowStart,
        arrowEnd: cd.arrowEnd,
      } satisfies ConnectorData,
    };

    const seg2: CanvasEntity = {
      id: seg2Id,
      kind: 'connector',
      x: 0, y: 0, width: 0, height: 0,
      layerId: '',
      connectable: false,
      zIndex: maxZ + 3,
      data: {
        from: wpEndpoint,
        to: cd.to,
        segmentType: cd.segmentType,
        strokeColor: cd.strokeColor,
        strokeWidth: cd.strokeWidth,
        strokeStyle: cd.strokeStyle,
        arrowStart: cd.arrowStart,
        arrowEnd: cd.arrowEnd,
      } satisfies ConnectorData,
    };

    const { [event.connectorId]: _, ...remainingConnectors } = ctx.connectors;

    return {
      ...ctx,
      nodes: { ...ctx.nodes, [event.waypointId]: newWaypoint },
      connectors: { ...remainingConnectors, [seg1Id]: seg1, [seg2Id]: seg2 },
      ...bumpVersions(ctx, 'nodes', 'connectors'),
    };
  },

  'connector.updateStyle': (ctx: CanvasStoreContext, event: {
    connectorId: string;
    changes: Partial<Pick<ConnectorData, 'strokeColor' | 'strokeWidth' | 'strokeStyle' | 'arrowStart' | 'arrowEnd' | 'label' | 'labelOffsetX' | 'labelOffsetY'>>;
  }): CanvasStoreContext => {
    const e = ctx.connectors[event.connectorId];
    if (!e || e.kind !== 'connector') return ctx;
    return {
      ...ctx,
      connectors: { ...ctx.connectors, [event.connectorId]: { ...e, data: { ...e.data, ...event.changes } } },
    };
  },

  'connector.batchRedock': (ctx: CanvasStoreContext, event: {
    connectorIds: string[];
    end: 'from' | 'to';
    newEndpoint: ConnectorEndpoint;
  }): CanvasStoreContext => {
    if (event.connectorIds.length === 0) return ctx;
    const newConnectors = { ...ctx.connectors };

    for (const id of event.connectorIds) {
      const e = newConnectors[id];
      if (!e || e.kind !== 'connector') continue;

      const cd = getConnectorData(e);
      const newFrom = event.end === 'from' ? event.newEndpoint : cd.from;
      const newTo = event.end === 'to' ? event.newEndpoint : cd.to;

      if (isSelfLoopEndpoint(newFrom, newTo)) {
        delete newConnectors[id];
        continue;
      }

      newConnectors[id] = { ...e, data: { ...e.data, from: newFrom, to: newTo } };
    }

    return { ...ctx, connectors: newConnectors, ...bumpVersions(ctx, 'connectors') };
  },
});

export type ConnectorCommand = CommandsOf<typeof connectorCommands>;
